import { FileText, Database, Globe, HelpCircle, Book, FileCheck, Package, Tag, Settings, BarChart3 } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/hooks/useLanguage";

const contentIcons = [Globe, HelpCircle, Book, FileCheck] as const;
const productIcons = [Package, Tag, Settings, BarChart3] as const;

const ZweiWissensartenSection = () => {
  const { ref, isVisible } = useScrollAnimation();
  const { t, tArray } = useLanguage();
  const contentItems = tArray("knowledgeBasePage.zweiArten.contentItems");
  const productItems = tArray("knowledgeBasePage.zweiArten.productItems");

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      className={cn(
        "py-16 md:py-24 transition-all duration-700 ease-out",
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      )}
    >
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4 text-sm font-semibold text-primary uppercase tracking-wider">
              {t("knowledgeBasePage.zweiArten.badge")}
            </Badge>

            <h2 className="text-3xl md:text-4xl font-semibold mb-2">
              <span className="text-foreground">{t("knowledgeBasePage.zweiArten.title")}</span>
            </h2>

            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              {t("knowledgeBasePage.zweiArten.intro")}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div
              className={cn(
                "bg-secondary/50 rounded-2xl p-8 border border-border transition-all",
                isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
              )}
              style={{ transitionDelay: isVisible ? "200ms" : "0ms", transitionDuration: "600ms" }}
            >
              <div className="w-14 h-14 rounded-xl bg-chathero-mint/20 flex items-center justify-center mb-6">
                <FileText className="w-7 h-7 text-chathero-mint" />
              </div>

              <h3 className="text-2xl font-bold text-primary mb-2">{t("knowledgeBasePage.zweiArten.contentTitle")}</h3>
              <p className="text-chathero-orange font-semibold mb-4">{t("knowledgeBasePage.zweiArten.contentSubtitle")}</p>
              <p className="text-muted-foreground mb-4 leading-relaxed">{t("knowledgeBasePage.zweiArten.contentDesc")}</p>

              <div className="bg-chathero-mint/10 rounded-lg p-3 mb-6">
                <p className="text-sm text-primary">
                  <span className="font-semibold">{t("knowledgeBasePage.zweiArten.contentExample")}</span> {t("knowledgeBasePage.zweiArten.contentExampleQ")}
                </p>
                <p className="text-xs text-muted-foreground mt-1">{t("knowledgeBasePage.zweiArten.contentExampleA")}</p>
              </div>

              <h4 className="font-semibold text-primary mb-4">{t("knowledgeBasePage.zweiArten.contentTypicalLabel")}</h4>
              <ul className="space-y-3">
                {contentItems.map((item, index) => {
                  const Icon = contentIcons[index];
                  return (
                    <li key={item} className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-chathero-mint/10 flex items-center justify-center">
                        <Icon className="w-4 h-4 text-chathero-mint" />
                      </div>
                      <span className="text-foreground">{item}</span>
                    </li>
                  );
                })}
              </ul>

              <div className="mt-6 pt-6 border-t border-border">
                <p className="text-sm text-muted-foreground">{t("knowledgeBasePage.zweiArten.contentFooter")}</p>
              </div>
            </div>

            <div
              className={cn(
                "bg-secondary/50 rounded-2xl p-8 border border-border transition-all",
                isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"
              )}
              style={{ transitionDelay: isVisible ? "400ms" : "0ms", transitionDuration: "600ms" }}
            >
              <div className="w-14 h-14 rounded-xl bg-chathero-orange/20 flex items-center justify-center mb-6">
                <Database className="w-7 h-7 text-chathero-orange" />
              </div>

              <h3 className="text-2xl font-bold text-primary mb-2">{t("knowledgeBasePage.zweiArten.productTitle")}</h3>
              <p className="text-chathero-orange font-semibold mb-4">{t("knowledgeBasePage.zweiArten.productSubtitle")}</p>
              <p className="text-muted-foreground mb-4 leading-relaxed">{t("knowledgeBasePage.zweiArten.productDesc")}</p>

              <div className="bg-chathero-orange/10 rounded-lg p-3 mb-6">
                <p className="text-sm text-primary">
                  <span className="font-semibold">{t("knowledgeBasePage.zweiArten.productExample")}</span> {t("knowledgeBasePage.zweiArten.productExampleQ")}
                </p>
                <p className="text-xs text-muted-foreground mt-1">{t("knowledgeBasePage.zweiArten.productExampleA")}</p>
              </div>

              <h4 className="font-semibold text-primary mb-4">{t("knowledgeBasePage.zweiArten.productTypicalLabel")}</h4>
              <ul className="space-y-3">
                {productItems.map((item, index) => {
                  const Icon = productIcons[index];
                  return (
                    <li key={item} className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-chathero-orange/10 flex items-center justify-center">
                        <Icon className="w-4 h-4 text-chathero-orange" />
                      </div>
                      <span className="text-foreground">{item}</span>
                    </li>
                  );
                })}
              </ul>

              <div className="mt-6 pt-6 border-t border-border">
                <p className="text-sm text-muted-foreground">{t("knowledgeBasePage.zweiArten.productFooter")}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ZweiWissensartenSection;
